import type {RouteLocationNormalizedGeneric, RouteLocationAsRelativeGeneric} from 'vue-router';
import {match, type MatchResult} from 'path-to-regexp';
import {flow, map, find} from '@dovca/toolbox';
import {storeToRefs} from 'pinia';
import {useStoryblokHypermarketsStore} from '~/stores/storyblokHypermarkets';
import {GsoaProductCatalogService} from '~/openapi';
import {createHouseProductSlug} from '~/utils/createHouseProductSlug';

type Redirect = {
	from: string;
	to: string;
	isProduct?: boolean
};

type MatchedRedirect = Redirect & {matched: MatchResult<Partial<Record<string, string | string[]>>>};

const redirectTemplates: Redirect[] = [
	{
		from: '/akcni-nabidka{/*category}/produkt-:vanr',
		to: 'branch-hypermarket-akcni-nabidka-category-p-product',
		isProduct: true,
	},
	{
		from: '/akcni-nabidka/*category',
		to: 'branch-hypermarket-akcni-nabidka-category',
	},
	{
		from: '/akcni-nabidka',
		to: 'branch-hypermarket-akcni-nabidka-category',
	},

	{
		from: '/cela-nabidka{/*category}/produkt-:vanr',
		to: 'branch-hypermarket-cela-nabidka-category-p-product',
		isProduct: true,
	},
	{
		from: '/cela-nabidka/*category',
		to: 'branch-hypermarket-cela-nabidka-category',
	},
	{
		from: '/cela-nabidka',
		to: 'branch-hypermarket-cela-nabidka-category',
	},
	{
		from: '/:branch/akcni-letaky/:slug',
		to: 'branch-letaky-slug',
	},
	{
		from: '/:branch/akcni-letaky',
		to: 'branch-letaky',
	},
	{
		from: '/akcni-letaky',
		to: 'branch-letaky',
	},
	{
		from: '/:branch/letaky/aktualni-letak',
		to: 'branch-letaky-aktualni',
	},
	{
		from: '/:branch/letaky/budouci-letak',
		to: 'branch-letaky-budouci',
	},
	{
		from: '/:branch/sluzby-a-produkty/obchodni-jednotky-k-pronajmu/:slug',
		to: 'branch-sluzby-obchodni-jednotky-k-pronajmu-slug',
	},
	{
		from: '/:branch/sluzby-a-produkty/:slug',
		to: 'branch-sluzby-slug',
	},
	{
		from: '/:branch/sluzby-a-produkty',
		to: 'branch-sluzby',
	},
];

const redirects = redirectTemplates.map((r) => ({
	...r,
	matched: match(r.from),
}));

export default defineNuxtRouteMiddleware(async (to) => {
	const redirect = findRedirect(to);

	if (!redirect) {
		return;
	}

	const createRouteLocation = useCreateRouteLocation(to);
	const routeLocation = await createRouteLocation(redirect);

	if (!routeLocation) {
		return;
	}

	return navigateTo(
		{
			...to,
			...routeLocation,
		},
		{redirectCode: 301},
	);
});

const findRedirect = (to: RouteLocationNormalizedGeneric): MatchedRedirect | null => {
	return (flow(
		redirects,
		map((r) => ({
			...r,
			matched: r.matched(to.path),
		})),
		find((r) => r.matched !== false),
	) ?? null) as MatchedRedirect | null;
};

const useCreateRouteLocation = (to: RouteLocationNormalizedGeneric) => {
	const {selectedHypermarketWithFallbackInstance} = storeToRefs(useStoryblokHypermarketsStore());
	const houseNumber = Number(selectedHypermarketWithFallbackInstance.value?.content.gsoaId);
	const newBranch = resolveBranch(to);

	return async (redirect: MatchedRedirect): Promise<RouteLocationAsRelativeGeneric | null> => {
		if (!redirect.isProduct) {
			return {
				name: redirect.to,
				params: {
					...redirect.matched.params,
					branch: newBranch,
				},
			};
		}

		try {
			const product = await GsoaProductCatalogService.getHouseProductByVanr({
				houseNumber,
				productId: redirect.matched.params.vanr as string,
			});

			if (!product) {
				return null;
			}

			const productSlug = createHouseProductSlug(product);

			// Remove unwanted parameters
			delete redirect.matched.params.vanr;
			delete redirect.matched.params.category;

			return {
				name: redirect.to,
				params: {
					...redirect.matched.params,
					branch: newBranch,
					product: productSlug,
				},
			};
		} catch (e: unknown) {
			throw createError({
				cause: e,
				message: 'Failed to fetch product for redirect.',
			});
		}
	};
};

const resolveBranch = (to: RouteLocationNormalizedGeneric) => {
	const {selectedHypermarket, hypermarketsWithGlobusBySlug} = storeToRefs(useStoryblokHypermarketsStore());

	// 1. If branch exists in route params, try resolving hypermarket slug
	const branchParam = to.params.branch;
	if (typeof branchParam === 'string' && branchParam.length > 0) {
		const hypermarket = hypermarketsWithGlobusBySlug.value?.[branchParam];
		if (hypermarket?.slug) {
			return branchParam;
		}
	}

	// 2. If path param exists, try resolving hypermarket slug
	const pathParam = to.params.path?.[0];
	if (pathParam) {
		const hypermarket = hypermarketsWithGlobusBySlug.value?.[pathParam];
		if (hypermarket?.slug) {
			return hypermarket.slug;
		}
	}

	// 3. Fallback to selected hypermarket
	if (selectedHypermarket.value) {
		return selectedHypermarket.value;
	}

	// 4. Final fallback
	return 'globus';
};
